import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import PdfIcon from '../../static/icons/pdf-icon.svg';
import DragIcon from '../../static/icons/drag-icon.svg';
import RedCrossIcon from '../../static/icons/red-cross.svg';
import SavingIcon from '../../static/icons/saving-icon.svg';
import { uploadStimulusFileToServer } from '../../service';
import Toast from '../Toast';
import { processStimulusFiles } from './processStimulusFiles';
import StimulusRichTextEditor from './StimulusRichTextEditor';
import { stimulusFileUrl } from '../../utils/stimulusFileUrl';

const buildStimulusFile = (file, url) => {
  const isPdf =
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  return {
    id: uuidv4(),
    name: file.name,
    url,
    mimeType: file.type || (isPdf ? 'application/pdf' : 'image/jpeg'),
    fileType: isPdf ? 'PDF' : 'IMAGE',
    extractedText: null,
    extractedImageUrls: [],
  };
};

const StimulusEditor = ({
  stimulusText,
  stimulusFiles = [],
  onStimulusTextChange,
  onStimulusFilesChange,
  defaultTab = 'write',
  isDisabled = false,
  uploadHint = '(Maximum file upload size: 15mb)',
  emptyUploadLabel = 'Select Files',
  addMoreLabel = 'Add More Files',
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isUploading, setIsUploading] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const validFiles = await processStimulusFiles(files);
    if (validFiles.length === 0) {
      return;
    }
    setIsUploading(true);
    try {
      const uploadedUrls = await Promise.all(
        validFiles.map((file) => uploadStimulusFileToServer(file))
      );
      const newFiles = validFiles.reduce((acc, file, index) => {
        const url = uploadedUrls[index];
        if (!url) {
          toast(<Toast message={`Failed to upload ${file.name}`} />);
          return acc;
        }
        acc.push(buildStimulusFile(file, url));
        return acc;
      }, []);
      if (newFiles.length > 0) {
        onStimulusFilesChange?.([...stimulusFiles, ...newFiles]);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }
    handleFiles(files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (isDisabled || isUploading) {
      return;
    }
    const files = Array.from(event.dataTransfer.files || []);
    handleFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleRemoveFile = (fileId) => {
    onStimulusFilesChange?.(stimulusFiles.filter((file) => file.id !== fileId));
  };

  const reorderFiles = (sourceId, targetId) => {
    const sourceIndex = stimulusFiles.findIndex((file) => file.id === sourceId);
    const targetIndex = stimulusFiles.findIndex((file) => file.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return stimulusFiles;
    }
    const nextFiles = [...stimulusFiles];
    const [movedFile] = nextFiles.splice(sourceIndex, 1);
    nextFiles.splice(targetIndex, 0, movedFile);
    return nextFiles;
  };

  const handleDragStart = (fileId) => (event) => {
    if (isDisabled || isUploading) {
      return;
    }
    setDraggedId(fileId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(fileId));
  };

  const handleDragOverItem = (fileId) => (event) => {
    if (isDisabled || draggedId === null || draggedId === fileId) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setDragOverId(fileId);
  };

  const handleDropItem = (fileId) => (event) => {
    if (isDisabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const sourceId = draggedId || event.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === fileId) {
      setDragOverId(null);
      setDraggedId(null);
      return;
    }
    const nextFiles = reorderFiles(sourceId, fileId);
    onStimulusFilesChange?.(nextFiles);
    setDragOverId(null);
    setDraggedId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const triggerFilePicker = () => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <EditorContainer
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <TabsHeader>
        <TabButton
          type="button"
          isActive={activeTab === 'write'}
          onClick={() => setActiveTab('write')}
          disabled={isDisabled || isUploading}
        >
          Write
        </TabButton>
        <TabButton
          type="button"
          isActive={activeTab === 'upload'}
          onClick={() => setActiveTab('upload')}
          disabled={isDisabled || isUploading}
        >
          Upload
        </TabButton>
      </TabsHeader>

      <TabContent>
        {activeTab === 'write' ? (
          <WriteArea>
            <StimulusRichTextEditor
              value={stimulusText}
              onChange={onStimulusTextChange}
              placeholder="Start here.."
            />
          </WriteArea>
        ) : (
          <UploadArea onDrop={handleDrop} onDragOver={handleDragOver}>
            {stimulusFiles.length > 0 && (
              <>
                <UploadTitle>Uploaded Files</UploadTitle>
                <FilesList>
                  {stimulusFiles.map((file) => (
                    <FileRow
                      key={file.id}
                      draggable={!isDisabled && !isUploading}
                      onDragStart={handleDragStart(file.id)}
                      onDragOver={handleDragOverItem(file.id)}
                      onDrop={handleDropItem(file.id)}
                      onDragEnd={handleDragEnd}
                      isDragOver={dragOverId === file.id}
                      isDragging={draggedId === file.id}
                    >
                      <DragHandle>
                        <DragIconImg src={DragIcon} alt="" aria-hidden="true" />
                      </DragHandle>
                      <FileThumb>
                        {file.fileType === 'IMAGE' ? (
                          <ThumbImage src={stimulusFileUrl(file.url)} alt={file.name} />
                        ) : (
                          <ThumbIcon src={PdfIcon} alt="PDF" />
                        )}
                      </FileThumb>
                      <FileMeta>
                        <FileName>{file.name}</FileName>
                        <FileType>
                          {file.fileType === 'PDF' ? 'PDF' : 'Image'}
                        </FileType>
                      </FileMeta>
                      {!isDisabled && !isUploading && (
                        <RemoveButton
                          type="button"
                          onClick={() => handleRemoveFile(file.id)}
                        >
                          <RemoveIcon
                            src={RedCrossIcon}
                            alt=""
                            aria-hidden="true"
                          />
                          Remove
                        </RemoveButton>
                      )}
                    </FileRow>
                  ))}
                </FilesList>
              </>
            )}

            <UploadAction>
              <HiddenInput
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={handleFileInputChange}
              />
              {isUploading ? (
                <UploadingIndicator>
                  <UploadingIconImg
                    src={SavingIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  Uploading files...
                </UploadingIndicator>
              ) : (
                <>
                  <UploadButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFilePicker();
                    }}
                    disabled={isDisabled}
                  >
                    {stimulusFiles.length > 0 ? addMoreLabel : emptyUploadLabel}
                  </UploadButton>
                  <UploadHint>Or drop files here</UploadHint>
                  <UploadHint>{uploadHint}</UploadHint>
                </>
              )}
            </UploadAction>
          </UploadArea>
        )}
      </TabContent>
    </EditorContainer>
  );
};

export default StimulusEditor;

const EditorContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fbf7fe;
  overflow: visible;
  display: flex;
  flex-direction: column;
`;

const TabsHeader = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 20px 0;
  background: #fbf7fe;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.isActive ? '#7200E0' : '#9A94A6')};
  padding-bottom: 12px;
  border-bottom: 2px solid
    ${(props) => (props.isActive ? '#7200E0' : 'transparent')};
  cursor: pointer;
`;

const TabContent = styled.div`
  background: #fff;
  padding: 16px 20px 20px;
`;

const WriteArea = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: visible;
  min-height: 220px;

  .ql-toolbar {
    border: none;
    border-bottom: 1px solid #e5e7eb;
    background: #f8f4fd;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
    position: static;
    top: auto;
    z-index: auto;
    margin-bottom: 0;
    overflow: visible;
  }

  .ql-toolbar .ql-formats {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-right: 0;
    overflow: visible;
  }

  .ql-toolbar button,
  .ql-toolbar .ql-picker,
  .ql-toolbar .ql-picker-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ql-toolbar .ql-picker {
    height: 32px;
  }

  .ql-toolbar .ql-picker-label {
    height: 32px;
  }

  .ql-toolbar .ql-formats button[class^='ql-']::after {
    top: calc(100% + 6px);
    bottom: auto;
    transform: translate(-50%, 0);
  }

  .ql-container {
    border: none;
    min-height: 180px;
    overflow: visible;
  }

  .ql-editor {
    min-height: 180px;
    padding: 16px;
  }

  .ql-editor.ql-blank::before {
    left: 16px;
    right: 16px;
  }
`;

const UploadArea = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  background: #fff;
`;

const UploadTitle = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
  color: #302d34;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${(props) => (props.isDragOver ? '#7200E0' : '#e5e7eb')};
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  opacity: ${(props) => (props.isDragging ? 0.6 : 1)};
  box-shadow: ${(props) =>
    props.isDragOver ? '0 0 0 2px rgba(114, 0, 224, 0.12)' : 'none'};
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
`;

const DragIconImg = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
`;

const FileThumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8f4fd;
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const FileMeta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileName = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 15px;
  font-weight: 600;
  color: #302d34;
`;

const FileType = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 13px;
  color: #6b6471;
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  font-family: var(--font-family-ibm_plex_sans);
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RemoveIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: block;
`;

const UploadAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const UploadButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  background: #7200e0;
  color: #fff;
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  cursor: pointer;
  min-width: 180px;
`;

const UploadHint = styled.div`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  color: #6b6471;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 15px;
  font-weight: 600;
  color: #7200e0;
`;

const UploadingIconImg = styled.img`
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
